import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { QueryApiService } from 'src/app/shared/services/customer-api/query-api.service';
import { ReportDialogData } from 'src/app/micro-components/dialog-report/dialog-report.component';
import { ReportApiService } from 'src/app/shared/services/customer-api/report-api.service';
import {
  ReportType,
  hasEnoughCompaniesForReport,
} from 'src/app/shared/utils/reports';

export type ReportScope = 'company' | 'portfolio' | 'query';

export interface ReportTypeState {
  available: boolean;
  status: string | null;
  loading: boolean;
}

export const INITIAL_REPORT_TYPE_STATE: ReportTypeState = {
  available: false,
  status: null,
  loading: false,
};

export interface ReportRegistryCtx {
  entity?: { type: string; alpha_id: string; name: string };
  folio?: { name: string; content: string[] } | null;
}

export interface ReportDeps {
  reportApi: ReportApiService;
  queryApi: QueryApiService;
  date: string;
}

export interface ReportDefinition {
  id: ReportType;
  scope: ReportScope;
  menuTitle: string;
  checkStatus(ctx: ReportRegistryCtx, deps: ReportDeps): Observable<string>;
  buildDialogData(
    ctx: ReportRegistryCtx,
    state: ReportTypeState,
    deps: ReportDeps,
  ): ReportDialogData;
}

const filterCompanies = (content: string[]): string[] =>
  content.filter((e) => e.split(':')[0] === 'company');

export const REPORT_REGISTRY: ReadonlyArray<ReportDefinition> = [
  {
    id: 'due_diligence_tabular',
    scope: 'company',
    menuTitle: 'Due Diligence (key events ranked by severity)',
    checkStatus: (ctx, { reportApi, date }) =>
      reportApi
        .getReportStatus(ctx.entity!.alpha_id, 'due_diligence_tabular', date)
        .pipe(map((res) => res['status'])),
    buildDialogData: (ctx, state) => {
      const entity = ctx.entity!;
      return {
        reportType: 'due_diligence_tabular',
        entityId: entity.alpha_id,
        entityType: entity.type,
        titleLine1: entity.name,
        titleLine2: 'Due Diligence',
        status: state.status,
        reportKind: 'due_diligence',
        reportLabel: 'Due Diligence',
        scoreTypeOptions: [
          { value: 'due_diligence_tabular', label: 'Credit' },
          { value: 'esgr_due_diligence_tabular', label: 'ESG' },
        ],
      };
    },
  },

  {
    id: 'due_diligence_tabular_portfolio',
    scope: 'portfolio',
    menuTitle: 'Due Diligence (key events ranked by severity)',
    checkStatus: (ctx, { reportApi, date }) => {
      const folio = ctx.folio;
      if (!folio || !hasEnoughCompaniesForReport(folio.content)) {
        return of('not_available');
      }
      return reportApi
        .getReportStatusPortfolio(
          folio.name,
          filterCompanies(folio.content),
          'due_diligence_tabular_portfolio',
          date,
        )
        .pipe(map((res) => res['status']));
    },
    buildDialogData: (ctx, state) => {
      const entity = ctx.entity!;
      const folio = ctx.folio!;
      const companies = filterCompanies(folio.content);
      return {
        reportType: 'due_diligence_tabular_portfolio',
        entityId: entity.alpha_id,
        entityType: entity.type,
        portfolioName: entity.name,
        portfolioUid: (folio as { uid?: string }).uid ?? entity.alpha_id,
        portfolioContent: companies,
        titleLine1: entity.name,
        titleLine2: 'Due Diligence (key events ranked by severity)',
        messageLine:
          companies.length !== folio.content.length
            ? "Your portfolio includes entities other than companies that won't be covered by the report."
            : undefined,
        status: state.status,
        reportKind: 'due_diligence',
        reportLabel: 'Due Diligence',
        scoreTypeOptions: [
          { value: 'due_diligence_tabular_portfolio', label: 'Credit' },
          {
            value: 'esgr_due_diligence_tabular_portfolio',
            label: 'ESG',
          },
        ],
      };
    },
  },

  {
    id: 'due_diligence_tabular',
    scope: 'query',
    menuTitle: 'Due Diligence (Detailed)',
    checkStatus: (_ctx, { queryApi }) =>
      of(queryApi.query ? 'available' : 'not_available'),
    buildDialogData: (_ctx, _state, { queryApi }) => {
      const query = queryApi.query;
      let searchQuery = query.query;
      const queryName = query.name || 'Search Query';
      if (typeof searchQuery === 'string') {
        searchQuery = {
          operator: 'or',
          filters: [
            {
              negated: false,
              field: 'text',
              name: searchQuery,
              value: searchQuery,
              input: searchQuery,
            },
          ],
          groups: [],
        };
      }
      return {
        reportType: 'due_diligence_tabular',
        searchQuery,
        queryName,
        titleLine1: queryName,
        titleLine2: 'Credit Due Diligence (Detailed)',
        status: 'available',
        reportKind: 'due_diligence',
        reportLabel: 'Credit Due Diligence',
      };
    },
  },
];

export function scopeOfContext(
  mode: 'entity' | 'query',
  entityType: string | undefined,
): ReportScope | null {
  if (mode === 'query') return 'query';
  if (entityType === 'company') return 'company';
  if (entityType === 'portfolio') return 'portfolio';
  return null;
}

export function isStatusAvailable(status: string | null): boolean {
  return (
    status !== null &&
    status !== 'not_enough_data' &&
    status !== 'not_available'
  );
}
